import React, { useEffect, useState } from 'react';
import { Box, MenuItem, Modal, TextField } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useToast } from 'use-toast-mui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { updateMemberPointHistory } from '../../../api/member-point';
import { getBranchManager } from '../../../api/staff';
import { MEMBER_ADDITION_INFO_KEY } from '../../../constants/queryKeys';
import { errorMessages } from '../../../utils/methods';
import { FormInputDropdown } from '../../../form-components/FormInputDropdown';
import { FormInputPrice } from '../../../form-components/FormInputPrice';
import { FormInputText } from '../../../form-components/FormInputText';
import { FormInputDate } from '../../../form-components/FormInputDate';
import {
  CancelButton,
  CloseIconWrapper,
  ContentSection,
  FormFieldLabel,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeaderTitle,
  SaveButton,
  dateInputStyleFlex,
  disabledInputStyle,
  dropdownStyle,
  textInputStyle,
} from '../../SalesManagment/modal/enterSaleInfoModal/EnterSaleInfoModalStyled';

const MEMO_MAX_LENGTH = 10;

const fieldSx = { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 };
const twoColSx = { display: 'flex', gap: '16px' };
const hintSx = { fontSize: 12, color: '#8c8c8c', lineHeight: 1.6 };

/**
 * 수기·일괄 포인트 내역 수정 — 판매 정보 수정 모달과 같은 틀(검정 헤더·카드 섹션·44px 인풋).
 * 회원상세 포인트 탭과 센터관리 포인트 관리 목록에서 공용.
 * 적립은 포인트·유효기간·내용·담당자, 차감은 내용·담당자만. 서버가 같은 규칙으로 다시 검사한다.
 */
const PointHistoryEditModal = ({ open, onClose, row, memberId, branchId, token }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isEarn = row?.type === 'EARN';

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: { staffUserId: '', point: '', memo: '', expireDate: null },
  });
  const [noExpire, setNoExpire] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    reset({
      staffUserId: row.staffUserId ?? '',
      point: row.point != null ? String(row.point) : '',
      memo: row.description ?? '',
      expireDate: row.expireDate ? dayjs(row.expireDate).toDate() : null,
    });
    setNoExpire(!row.expireDate);
  }, [open, row, reset]);

  const { data: staffOptions = [] } = useQuery({
    queryKey: ['branchManager', branchId],
    queryFn: async () => {
      const data = await getBranchManager(branchId, token);
      return (data || []).map(item => ({ value: item.managerId, label: item.managerName }));
    },
    enabled: open && !!branchId && !!token,
  });

  const mutation = useMutation({
    mutationFn: payload => updateMemberPointHistory(memberId, row.pointHistoryId, payload, token),
    onSuccess: () => {
      toast.success(t('내역을 수정했습니다'));
      queryClient.invalidateQueries({ queryKey: ['memberPoints', memberId, branchId] });
      queryClient.invalidateQueries({ queryKey: MEMBER_ADDITION_INFO_KEY(memberId, branchId) });
      // 센터관리 > 포인트 관리 목록에서도 같은 모달을 쓴다
      queryClient.invalidateQueries({ queryKey: ['branchPointHistories', branchId] });
      onClose();
    },
    onError: error => toast.error(errorMessages(error)),
  });

  const watchedPoint = watch('point');
  const watchedExpire = watch('expireDate');
  const pointChanged = isEarn && Number(String(watchedPoint ?? '').replace(/,/g, '')) !== Number(row?.point);

  const handleSave = handleSubmit(values => {
    const pointNum = Number(String(values.point).replace(/,/g, '')) || 0;
    if (isEarn && pointNum <= 0) {
      toast.error(t('적립 포인트를 입력해 주세요'));
      return;
    }
    if (isEarn && !noExpire && !values.expireDate) {
      toast.error(t('만료일을 선택하거나 무기한을 선택해 주세요'));
      return;
    }
    mutation.mutate({
      point: isEarn ? pointNum : undefined,
      expireDate: isEarn && !noExpire ? dayjs(values.expireDate).format('YYYY-MM-DD') : undefined,
      noExpire: isEarn && noExpire ? true : undefined,
      memo: values.memo ?? '',
      staffUserId: values.staffUserId || undefined,
    });
  });

  if (!row) return null;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="point-history-edit-title">
      <ModalContainer>
        <ModalHeader>
          <ModalHeaderTitle id="point-history-edit-title">{isEarn ? t('적립 내역 수정') : t('차감 내역 수정')}</ModalHeaderTitle>
          <CloseIconWrapper onClick={onClose}>
            <Close sx={{ width: '20px', height: '20px' }} />
          </CloseIconWrapper>
        </ModalHeader>

        <ModalContent>
          {/* 카드 1 — 어떤 내역인지 (읽기 전용) */}
          <ContentSection>
            <Box sx={twoColSx}>
              <Box sx={{ ...fieldSx, flex: 1 }}>
                <FormFieldLabel>{t('적용 일시')}</FormFieldLabel>
                <TextField size="small" value={dayjs(row.regDate).format('YYYY-MM-DD HH:mm')} disabled sx={{ ...disabledInputStyle, width: '100%' }} />
              </Box>
              <Box sx={{ ...fieldSx, flex: 1 }}>
                <FormFieldLabel>{t('구분')}</FormFieldLabel>
                <TextField
                  size="small"
                  value={`${isEarn ? t('적립') : t('차감')} ${Number(row.point || 0).toLocaleString()} P`}
                  disabled
                  sx={{ ...disabledInputStyle, width: '100%' }}
                />
              </Box>
            </Box>
          </ContentSection>

          {/* 카드 2 — 수정 항목 */}
          <ContentSection>
            <Box sx={twoColSx}>
              <Box sx={{ ...fieldSx, flex: 1 }}>
                <FormFieldLabel>{t('담당자')}</FormFieldLabel>
                <FormInputDropdown
                  control={control}
                  name="staffUserId"
                  options={staffOptions}
                  sx={{ ...dropdownStyle, width: '100%' }}
                  disabledItem={<MenuItem disabled value="">{t('담당자 선택')}</MenuItem>}
                />
              </Box>
              {isEarn && (
                <Box sx={{ ...fieldSx, flex: 1 }}>
                  <FormFieldLabel>{t('적립 포인트')}</FormFieldLabel>
                  <FormInputPrice control={control} name="point" size="small" placeholder={t('숫자 입력')} sx={{ ...textInputStyle, width: '100%' }} InputProps={{ endAdornment: <span style={{ color: '#8c8c8c', fontSize: 14 }}>P</span> }} />
                </Box>
              )}
            </Box>

            {isEarn && (
              <Box sx={fieldSx}>
                <FormFieldLabel>{t('유효기간')}</FormFieldLabel>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0, opacity: noExpire ? 0.5 : 1 }}>
                    <FormInputDate
                      name="expireDate"
                      control={control}
                      disabled={noExpire}
                      minDate={dayjs().toDate()}
                      placeholder={t('만료일 선택')}
                      renderInput={params => <TextField size="small" variant="outlined" {...params} sx={{ ...dateInputStyleFlex, width: '100%' }} />}
                    />
                  </Box>
                  <Box
                    onClick={() => {
                      const next = !noExpire;
                      setNoExpire(next);
                      if (next) setValue('expireDate', null);
                    }}
                    sx={{
                      height: 44,
                      px: '16px',
                      borderRadius: '4px',
                      border: `1px solid ${noExpire ? '#4ab3bc' : '#a6a6a6'}`,
                      background: noExpire ? '#f0fafa' : '#fff',
                      color: noExpire ? '#4ab3bc' : '#141414',
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                    }}>
                    {t('무기한')}
                  </Box>
                </Box>
                <Box sx={hintSx}>
                  {noExpire
                    ? t('만료되지 않는 포인트로 저장됩니다.')
                    : watchedExpire
                      ? `${dayjs(watchedExpire).format('YYYY-MM-DD')} ${t('까지 사용할 수 있습니다.')}`
                      : t('만료일을 고르거나 무기한을 선택하세요.')}
                </Box>
              </Box>
            )}

            <Box sx={fieldSx}>
              <FormFieldLabel>{t('내용')}</FormFieldLabel>
              <FormInputText control={control} name="memo" placeholder={t('10자 이내로 작성해 주세요.')} size="small" sx={{ ...textInputStyle, width: '100%' }} inputProps={{ maxLength: MEMO_MAX_LENGTH }} />
            </Box>

            {isEarn && (
              <Box sx={{ ...hintSx, background: '#F7F8FA', border: '1px solid #EDEFF3', borderRadius: '8px', p: '10px 12px' }}>
                {t('포인트를 바꾸면 회원 잔액과 이후 내역의 잔액 표시가 함께 조정됩니다. 이미 사용한 포인트보다 적게 줄일 수는 없습니다.')}
                {pointChanged && <b style={{ color: '#4ab3bc', marginLeft: 4 }}>{t('(포인트 변경됨)')}</b>}
              </Box>
            )}
          </ContentSection>
        </ModalContent>

        <ModalFooter>
          <CancelButton onClick={onClose}>{t('취소')}</CancelButton>
          <SaveButton onClick={handleSave} disabled={mutation.isPending}>
            {t('저장')}
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

export default PointHistoryEditModal;
